// src/pages/TrackCreation/Step1.tsx
import React, { useEffect } from "react";
import { uploadFileToS3Complete } from "../../../../api/s3";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RouteParamsWithLanguage, WaypointPictureProps } from "../../../../types/screensTypes";
import Button from "../../../../components/ui/Button";
import { getWaypointById } from "../../../../api";
import { FileInput } from "../../../../components/common/FileInput";
import TitleSubtitle from "../../../../components/common/TitleSubtitle";
import ImageList from "../../../../components/images/ImageList";
import { useWaypointChangeDetection } from "../../../../hooks/useWaypointChangeDetection";
import { useAddonAccess } from "../../../../hooks/useAddonAccess";
import { IMAGE_FORMAT, IMAGE_MAX_SIZE_200_KB } from "../../../../utils/image";
import ImageSpecsBadges from "../../../../components/common/ImageSpecsBadges";
import { useImageValidation } from "../../../../hooks/useImageValidation";
import stepStyles from "../createStep.module.css";
import wp from "./WaypointEdit.module.css";

const WaypointPicture: React.FC<WaypointPictureProps> = ({ navigateToNext }) => {
    const { wptId, language } = useParams<RouteParamsWithLanguage>();

    const { hasAccess: hasVideoAddon } = useAddonAccess("VIDEO");

    const {
        value: pictures,
        setValue: setPictures,
        saveIfChanged,
        setOriginalValue,
    } = useWaypointChangeDetection<string[]>({
        wptId: wptId || "",
        language,
        initialValue: [],
    });
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const imageValidation = useImageValidation({
        type: "waypoint",
        imageUrl: pictures[pictures.length - 1],
    });

    const handleFileSelected = async (files: File | File[]) => {
        const file = Array.isArray(files) ? files[0] : files;

        if (file) {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("No authentication token found");
                return;
            }

            await imageValidation.validateImage(file);

            uploadFileToS3Complete(
                {
                    file,
                    isPublic: true,
                    isFolder: false,
                },
                token,
            )
                .then((result: string) => {
                    setPictures((prevPictures) => [...prevPictures, result]);
                })
                .catch((error) => {
                    console.log(error);
                });
        }
    };

    const handleSaveChanges = async (): Promise<void> => {
        await saveIfChanged({ pictures });
        if (!wptId || !language) return;
        navigateToNext(wptId, language);
    };

    const getWaypoint = async (id: string): Promise<void> => {
        const token = localStorage.getItem("token");
        if (!token || !language) return;
        const waypointData = await getWaypointById({ token, id, language });
        let loadedPictures: string[] = [];

        if (waypointData) {
            const waypointArray = waypointData;
            if (waypointArray.waypointTemporary) {
                // The temporary (draft) waypoint is the source of truth. An empty
                // pictures array means the user removed all pictures in the current
                // draft, so we must not fall back to the permanent waypoint's pictures
                // here (that would re-add the removed photos).
                const picturesData = waypointArray.waypointTemporary.pictures;
                if (Array.isArray(picturesData) && picturesData.length > 0) {
                    loadedPictures = picturesData
                        .sort((a: { position: number }, b: { position: number }) => a.position - b.position)
                        .map((picture: { url: string }) => picture.url);
                }
            } else {
                const waypoint = waypointArray.waypoint;
                if (waypoint && waypoint.pictures && waypoint.pictures.length > 0) {
                    // Permanent waypoint stores Picture objects: sort by position then map to URLs.
                    loadedPictures = waypoint.pictures
                        .sort((a: { position: number }, b: { position: number }) => a.position - b.position)
                        .map((picture: { url: string }) => picture.url);
                }
            }
        }

        setPictures(loadedPictures);
        setOriginalValue(loadedPictures);
    };

    useEffect(() => {
        if (wptId && wptId !== "") {
            getWaypoint(wptId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wptId, language]);

    return (
        <div className={`${stepStyles.editScreen} ${wp.screen}`}>
            <TitleSubtitle
                title={t("addPhotosToYourAudioTour")}
                subtitle={t("imagesHelpVisitorsVisualizeJoiningYourActivity")}
                layout="edit"
            />

            {hasVideoAddon && (
                <div className={wp.note} role="alert">
                    {t("whenVideoSetImagesNotNecessary")}
                </div>
            )}

            <h2 className={wp.secH}>{t("requirements")}</h2>
            <ImageSpecsBadges spec="waypoint" warnings={imageValidation.warnings} />
            <p className={wp.reqText}>✕ {t("noPhotographerWatermarksLogosOrReadableLicensePlates")}</p>

            <h2 className={wp.secH}>{t("uploadYourPhotos")}</h2>
            <p className={wp.secHint}>{t("dragYourPhotosIntoTheAreaBelowOrSelectUploadPhotos")}</p>
            <div className={wp.dropzone}>
                <FileInput
                    onFileSelected={handleFileSelected}
                    showButton={true}
                    acceptedFileTypes={IMAGE_FORMAT}
                    maxSize={IMAGE_MAX_SIZE_200_KB}
                    maxSizeLabel={t("imageMaxSize200kb")}
                />
            </div>

            <div className={wp.gallery}>
                <ImageList pictures={pictures} setPictures={setPictures} />
            </div>

            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                    label={t("continue")}
                    onClick={handleSaveChanges}
                />
            </div>
        </div>
    );
};

export default WaypointPicture;
